/**
 * Unified sorting utility for eSIM plans
 * Eliminates code duplication across components
 */

/**
 * Parse validity string to days for sorting
 * @param {string} validity - Validity string like "30 days", "7 days", etc.
 * @returns {number} Number of days
 */
function parseValidityToDays(validity) {
    if (!validity) return 30;
    const match = validity.match(/(\d+)/);
    return match ? parseInt(match[1]) : 30;
}

/**
 * Sort eSIM plans by specified criteria
 * @param {Array} plans - Array of eSIM plans
 * @param {Object} sortConfig - Sort configuration
 * @param {string} sortConfig.key - Sort key ('data', 'validity', 'price', 'provider')
 * @param {string} sortConfig.direction - Sort direction ('ascending' or 'descending')
 * @returns {Array} Sorted array of plans
 */
export function sortPlans(plans, sortConfig) {
    if (!sortConfig.key || !plans?.length) return plans;

    return [...plans].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
            case 'data':
                // Handle different data types: unlimited, pay-as-you-go, regular
                aValue = a.isUnlimited ? 999999 : (a.isPayAsYouGo ? 0 : a.dataAmount || 0);
                bValue = b.isUnlimited ? 999999 : (b.isPayAsYouGo ? 0 : b.dataAmount || 0);
                break;

            case 'validity':
                aValue = parseValidityToDays(a.validity || '');
                bValue = parseValidityToDays(b.validity || '');
                break;

            case 'price':
                // Handle different price fields
                aValue = parseFloat(a.priceAmount || a.price || 0);
                bValue = parseFloat(b.priceAmount || b.price || 0);
                break;

            case 'provider':
                aValue = (a.provider || '').toLowerCase();
                bValue = (b.provider || '').toLowerCase();
                break;

            case 'pricePerUnit':
                // Calculate price per GB or per day
                if (a.isPayAsYouGo) {
                    aValue = parseFloat(a.pricePerGb || 10.00);
                } else if (a.isUnlimited) {
                    aValue = parseFloat(a.pricePerDay || (a.price / parseValidityToDays(a.validity)));
                } else {
                    aValue = parseFloat(a.pricePerGb || (a.price / a.dataAmount));
                }

                if (b.isPayAsYouGo) {
                    bValue = parseFloat(b.pricePerGb || 10.00);
                } else if (b.isUnlimited) {
                    bValue = parseFloat(b.pricePerDay || (b.price / parseValidityToDays(b.validity)));
                } else {
                    bValue = parseFloat(b.pricePerGb || (b.price / b.dataAmount));
                }
                break;

            default:
                return 0;
        }

        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            const result = aValue.localeCompare(bValue);
            return sortConfig.direction === 'ascending' ? result : -result;
        }

        // Handle numeric comparison
        if (aValue < bValue) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });
}

/**
 * Get sort icon for UI
 * @param {string} currentKey - Current sort key
 * @param {string} targetKey - Target sort key
 * @param {string} direction - Current sort direction
 * @returns {string} Sort icon
 */
export function getSortIcon(currentKey, targetKey, direction) {
    if (currentKey !== targetKey) return '↕';
    return direction === 'ascending' ? '↑' : '↓';
}

/**
 * Toggle sort direction
 * @param {string} currentKey - Current sort key
 * @param {string} targetKey - Target sort key
 * @param {string} currentDirection - Current sort direction
 * @returns {string} New sort direction
 */
export function toggleSortDirection(currentKey, targetKey, currentDirection) {
    if (currentKey === targetKey) {
        return currentDirection === 'ascending' ? 'descending' : 'ascending';
    }
    return 'ascending';
}
